/**
 * Tenant-wide stats slice. Fetches counts for prospects, customers,
 * contacts, resellers, leads, tasks, recent activity, and overdue
 * invoices in a single call to brain's `/api/v1/dashboard/stats`
 * endpoint (the endpoint name is brain-side and stays as is).
 *
 * Originally fed a CRM dashboard page, which has been removed; the
 * slice now powers the count badges in the CRM sidebar (Prospects,
 * Customers, Resellers, Contacts) and the task-count badge.
 */
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { statsApi } from '../api'

export interface TenantStats {
  prospects: number
  // Per-origin prospect counts — the two Prospects pages partition the leads
  // (regular = not_ai_generated, AI = ai_generated); together they equal
  // `prospects`. Drive the CRM sidebar's "Prospects" / "AI Prospects" badges.
  ai_prospects?: number
  regular_prospects?: number
  customers: number
  contacts?: number
  resellers?: number
  totals: {
    leads: number
    ai_leads?: number
    regular_leads?: number
    active_leads: number
    tasks: number
  }
  lead_pipeline: { [key: string]: number }
  task_stats: {
    total: number
    completed: number
    pending: number
    overdue: number
  }
  recent_activity: {
    activities: number
    tasks: number
  }
  overdue_invoices?: number // Only present if user has invoice permissions
  unpaid_invoices?: number // backward compatibility
  generated_at: string
}

interface TenantStatsState {
  stats: TenantStats | null
  loading: boolean
  error: string | null
  lastFetched: number | null
  autoRefresh: boolean
  /** Consecutive failed fetches. Drives {@link retryBackoffMs}; reset on any
   * success. See the module header for why this exists. */
  failureCount: number
  /** When the most recent failure landed (epoch ms), or null after a success.
   * `lastFailedAt + retryBackoffMs(failureCount)` is the earliest moment the
   * poller is allowed to try again. */
  lastFailedAt: number | null
}

const initialState: TenantStatsState = {
  stats: null,
  loading: false,
  error: null,
  lastFetched: null,
  autoRefresh: true,
  failureCount: 0,
  lastFailedAt: null
}

/**
 * How long to wait after `failureCount` consecutive failures before the
 * auto-refresh poller may try again. Exponential, capped at 5 minutes — long
 * enough that a hard failure (401 before the crm-api mirror exists, a 403 from
 * rack-attack, crm-api restarting) costs a handful of requests instead of
 * thousands, short enough that the badges heal on their own once the cause
 * clears, with no reload.
 *
 * Pure + exported so the backoff schedule is unit-testable without React.
 */
export function retryBackoffMs(failureCount: number): number {
  if (failureCount <= 0) return 0
  const step = Math.min(failureCount, 5)
  return Math.min(30_000 * 2 ** (step - 1), 300_000)
}

export const fetchTenantStats = createAsyncThunk(
  'tenantStats/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await statsApi.getStats()
      return response.data
    } catch (error: any) {
      console.error('❌ Tenant stats error:', error)
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tenant stats')
    }
  }
)

export const updateStatCount = createAsyncThunk(
  'tenantStats/updateCount',
  async ({ stat, increment }: { stat: 'prospects' | 'customers'; increment: number }, { rejectWithValue }) => {
    try {
      return { stat, increment }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update stat count')
    }
  }
)

const tenantStatsSlice = createSlice({
  name: 'tenantStats',
  initialState,
  reducers: {
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    incrementStat: (state, action: PayloadAction<{ stat: 'prospects' | 'customers'; amount?: number }>) => {
      if (state.stats) {
        const { stat, amount = 1 } = action.payload
        state.stats[stat] += amount
      }
    },
    decrementStat: (state, action: PayloadAction<{ stat: 'prospects' | 'customers'; amount?: number }>) => {
      if (state.stats) {
        const { stat, amount = 1 } = action.payload
        state.stats[stat] = Math.max(0, state.stats[stat] - amount)
      }
    },
    resetStats: (state) => {
      state.stats = null
      state.loading = false
      state.error = null
      state.lastFetched = null
      state.failureCount = 0
      state.lastFailedAt = null
    },
    forceRefresh: (state) => {
      state.lastFetched = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenantStats.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTenantStats.fulfilled, (state, action) => {
        state.loading = false
        state.stats = action.payload
        state.lastFetched = Date.now()
        state.error = null
        state.failureCount = 0
        state.lastFailedAt = null
      })
      .addCase(fetchTenantStats.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        // Count the failure and stamp it so the poller can back off. Without
        // this the hook re-fetched the instant `loading` fell back to false —
        // the 2026-08-24 request storm. See useTenantStats.
        state.failureCount += 1
        state.lastFailedAt = Date.now()
      })
      .addCase(updateStatCount.fulfilled, (state, action) => {
        if (state.stats) {
          const { stat, increment } = action.payload
          state.stats[stat] += increment
        }
      })
  }
})

export const {
  setAutoRefresh,
  clearError,
  incrementStat,
  decrementStat,
  resetStats,
  forceRefresh
} = tenantStatsSlice.actions

export default tenantStatsSlice.reducer

// Selectors
export const selectTenantStats = (state: { tenantStats: TenantStatsState }) => state.tenantStats.stats
export const selectTenantStatsLoading = (state: { tenantStats: TenantStatsState }) => state.tenantStats.loading
export const selectTenantStatsError = (state: { tenantStats: TenantStatsState }) => state.tenantStats.error
export const selectLastFetched = (state: { tenantStats: TenantStatsState }) => state.tenantStats.lastFetched
export const selectAutoRefresh = (state: { tenantStats: TenantStatsState }) => state.tenantStats.autoRefresh
export const selectFailureCount = (state: { tenantStats: TenantStatsState }) => state.tenantStats.failureCount
export const selectLastFailedAt = (state: { tenantStats: TenantStatsState }) => state.tenantStats.lastFailedAt
